import React, { Component } from 'react'
import styled from 'styled-components'
import flatten from 'lodash/flatten'
import get from 'lodash/get'
import memoize from 'lodash/memoize'
import classNames from 'classnames'
import { Flex, Box, Text, Icon, getPaletteColor, Absolute } from 'pcln-design-system'
import PropTypes from 'prop-types'
import { retrieveTripType } from '@pcln/flodash'

import OperatedBy from '../OperatedBy'
import Slice from '../Slice'
import getFreeCancellationMessage from '../../util/get-free-cancellation-message'
import FareBrandShelf from '../FareBrandShelf/FareBrandShelfNGS'
import getSubjectToGovtApproval from '../../util/get-subject-to-government-approval/index'
import getUpsellOptionsBySlice from '../FareBrandDrawer/getUpsellOptionsBySlice'
import { getFusedFareBrands } from '../FareBrandDrawer/getUpsellOptionsBySlice/helpers'
import { baseFareCarriers } from '../../constants'
import { getMerchandisingText } from '../../util/getMerchandisingText/getMerchandisingText'
import {
  getFareBrandList,
  getCompareModalData,
  getStopText,
  getFareBrandListForBaseAirline,
  updateFareBrandsWithBackupBrandAttributes,
  getMissingFareBrandAttributes,
} from '../FareBrandShelf/helpers'
import CompareOptionsModal from '../FareBrandShelf/CompareOptionsModal'

import ItineraryShelfBottomZone from './ItineraryShelf/ItineraryShelfBottomZone'

const isBaseFareOnly = memoize((airlineCode) => baseFareCarriers.includes(airlineCode))

const StyledItinerary = styled.li`
  font-family: 'Montserrat', San Francisco, Roboto;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: flex-start;
  cursor: auto;
  background-color: ${getPaletteColor('background.lightest')};
  padding: 0;
  &:last-child {
    margin-bottom: 0;
  }
`

const Zone = styled(Flex)`
  width: 100%;
`

const MainZone = styled(Zone)`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  border: none;
  padding: 0;
  position: relative;
`

const ItineraryInfo = styled(Flex)`
  flex-direction: column;
  border: solid ${getPaletteColor('border.base')} 1px;
  border-right-color: transparent;
  border-radius: ${(props) => (props.shouldShowBottomZone ? '6px 0px 0px 0px' : '6px 0px 0px 6px')};
  position: relative;
`

const MemberDealText = styled(Text.p)`
  padding-left: 5px;
  color: ${getPaletteColor('primary.dark')};
  font-size: 11px;
  font-weight: bold;
  padding-right: 4px;
  padding-top: 2px;
  ${(props) => props.theme.flyQueries.desktoplg} {
    font-size: 12px;
  }
`

const SignInText = styled(Text.span)`
  font-size: 11px;
  ${(props) => props.theme.flyQueries.desktoplg} {
    font-size: 12px;
  }
`

const SignInHoverText = styled(SignInText)`
  &:hover {
    text-decoration: underline;
  }
`

const StyledItinSlices = styled(Box)`
  margin-right: 2px;
`

const GovernmentApproval = styled(Text.p)`
  display: ${(props) => (props.isSubjectToGovtApproval ? 'block' : 'none')};
  padding: 0 12px 8px 12px;
  color: ${getPaletteColor('text.light')};
  margin-bottom: 0;
  font-size: 10px;
  font-weight: 300;

  ${(props) => props.theme.flyQueries.desktop} {
    padding: 0 16px 8px 16px;
  }
`

const MerchandisingAbsolute = styled(Absolute)`
  border: solid ${getPaletteColor('border.base')} 1px;
  display: none;
  ${(props) => props.theme.flyQueries.tabletlg} {
    display: inline-block;
  }
`

export function validateUpsells(upsells) {
  // When data is missing, just show default case
  function validateBrandAttributes(brandAttributes) {
    if (brandAttributes && brandAttributes.length > 0) {
      for (let i = 0; i < brandAttributes.length; i++) {
        if (!brandAttributes[i].type || !brandAttributes[i].description || !brandAttributes[i].inclusion) {
          return false
        }
      }
      return true
    }
    return false
  }

  for (let i = 0; i < 3 && i < upsells.length; i++) {
    if (
      !upsells[i].brandId ||
      !upsells[i].name ||
      !upsells[i].price ||
      !validateBrandAttributes(upsells[i].brandAttributes)
    ) {
      return []
    }
  }
  return upsells
}

class ItineraryShelf extends Component {
  getChildContext = () => ({
    gaIndex: this.props.gaIndex,
  })

  itinAirlines =
    get(this, 'props.itinerary.airlines', []) &&
    this.props.itinerary.airlines.map((airline) => airline.marketingAirline)

  isSingleShelf = get(this.context, 'SETI.AIR_BRANDED_FARES_SEARCH_SINGLE_SHELF.isVariant', false)

  render() {
    const {
      itinerary,
      gaIndex,
      hasNoHoverTooltipCookie,
      setNoTooltipOnHoverCookie,
      isUserLoggedIn,
      openLoginModal,
      position,
      airportToCity,
      brandReference,
      slices: rawSlices,
      departingFrom,
      shelfVariantName,
    } = this.props

    const { airlines, openJaw, merchandising } = itinerary

    // Get merchandising text
    const itinMerchandising = getMerchandisingText({
      merchandisingList: merchandising,
      flyGaLogic: this.context.flyGaLogic,
      departingFrom,
    })

    const tripType = retrieveTripType(itinerary.slices)
    const imageUrl = 'https://s1.pclncdn.com/design-assets/fly/carrier-logos/'

    // DATA_SORT_FLIGHT_SERVICE_INFRASTRUCTURE_AA_TEST - AIR-14779
    const bestValueFlight = itinMerchandising && itinMerchandising.label === 'BEST_VALUE'

    this.itinAirlines = [...new Set(this.itinAirlines)]
    const {
      alternateDatesTexts,
      voidWindowInfo,
      isInterline,
      isFused,
      totalPriceWithDecimal,
      operatedByAirlines,
      isSaleEligible,
      alternateType,
    } = itinerary

    const isSubjectToGovtApproval = getSubjectToGovtApproval(itinerary)
    const isAltDates = !!alternateDatesTexts
    const { onSelect, flyGaLogic } = this.context

    const handleClick = ({ token, cabinNames, gaAction, gaLabel }) => {
      onSelect({
        id: itinerary.id,
        gaIndex,
        itinerary,
        upsellToken: token,
        cabinNames,
      })
      flyGaLogic.fire.fireGenericListingsEvent(gaAction || 'Clicking upsell NGS', gaLabel || 'fly-listings')
    }

    const upsellOptionsBySlice = getUpsellOptionsBySlice(itinerary, brandReference, rawSlices)

    const fusedFareBrandOptions = isFused ? getFusedFareBrands(itinerary, brandReference) : []

    const upsells = validateUpsells(flatten(upsellOptionsBySlice))

    const slices = itinerary.slices.map((slice, index) => (
      <Slice
        airlines={airlines}
        itinIndex={gaIndex}
        key={index}
        slice={slice}
        sliceIndex={index}
        imageUrl={imageUrl}
        tripType={tripType}
        openJaw={openJaw}
        isAltDates={isAltDates}
        hasNoHoverTooltipCookie={hasNoHoverTooltipCookie}
        setNoTooltipOnHoverCookie={setNoTooltipOnHoverCookie}
        airportToCity={airportToCity}
        GDSCitySuggestion={slice.GDSCitySuggestion}
        alternateType={alternateType}
        numberOfUpsells={upsells.length}
        showFareBrandShelf
      />
    ))

    const showFlightSale = isSaleEligible

    const displayMerchandising = itinMerchandising && itinMerchandising.shortText && !showFlightSale
    const isCugEligible = itinerary.isCugEligible
    let cugStrikeThroughPrice = 0

    if (showFlightSale || (isCugEligible && isUserLoggedIn)) {
      cugStrikeThroughPrice = get(itinerary, 'strikethroughPrice', 0)
    }

    // ToDo: This calculations have to be in listings Data Composer
    const { dollar, cents } = totalPriceWithDecimal
    let baseFare = parseFloat(`${dollar.replace(/\$|,/g, '')}.${cents}`)
    const savingsPercent =
      cugStrikeThroughPrice > 0
        ? Math.floor(((cugStrikeThroughPrice - baseFare) / cugStrikeThroughPrice) * 100)
        : 0
    let savingsDollars = cugStrikeThroughPrice > 0 ? cugStrikeThroughPrice - baseFare : 0

    baseFare = baseFare ? baseFare.toFixed(2) : baseFare

    // Only show decimals if non-zero
    if (savingsDollars % 1 !== 0) {
      savingsDollars = savingsDollars.toFixed(2)
    }

    const isCUGinMerchandisingSection = false

    const cugMerchandisingSection = isCugEligible && isCUGinMerchandisingSection && (
      <Flex align='center' alignItems='center' data-test='cug-merchandising-section'>
        <Icon
          legacy={false}
          name={isUserLoggedIn ? 'unlock' : 'lock'}
          color='primary.dark'
          data-test='cug-lock'
          data-classname={isUserLoggedIn ? 'unlock' : 'lock'}
        />
        <MemberDealText>Member Deal</MemberDealText>
        {isUserLoggedIn ? (
          <SignInText>
            Signing in saved you ${savingsDollars}, that&apos;s {savingsPercent}%.
          </SignInText>
        ) : (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
          <span onClick={openLoginModal}>
            <SignInHoverText color='primary.base'>Sign in</SignInHoverText>
            <SignInText>&nbsp;to save on this flight.</SignInText>
          </span>
        )}
      </Flex>
    )

    let merchandisingSection = displayMerchandising && (
      <MerchandisingAbsolute top={7} right={15} bg={'background.light'} p={0} pl={1} pr={1}>
        <Flex>
          <Box mt={'2px'}>
            <Text data-test='merchandising-section' color='alert.base' fontSize={'10px'} bold caps>
              {itinMerchandising.shortText}
            </Text>
          </Box>
        </Flex>
      </MerchandisingAbsolute>
    )

    if (cugMerchandisingSection) {
      merchandisingSection = cugMerchandisingSection
    }

    const itineraryLevelCssClasses = classNames('pcln1-retail-flight', {
      'pcln1-fused-itin': isFused,
      'pcln1-interline-itin': isInterline,
      'pcln1-open-jaw-itin': itinerary.openJaw !== null,
    })

    const freeCancellationMessage = voidWindowInfo ? getFreeCancellationMessage(voidWindowInfo) : null

    const shouldShowBottomZone = freeCancellationMessage || itinerary.showDeltaFreeMessaging

    const airlineCode = itinerary.airlines[0].marketingAirline

    let fareBrands = isBaseFareOnly(airlineCode)
      ? getFareBrandListForBaseAirline(baseFare, airlineCode)
      : getFareBrandList(
          upsells,
          airlineCode,
          itinerary,
          brandReference,
          shelfVariantName,
          this.context.flyGaLogic
        )

    const missingBrandAttributes = getMissingFareBrandAttributes(fareBrands)

    if (missingBrandAttributes) {
      fareBrands = updateFareBrandsWithBackupBrandAttributes(fareBrands, airlineCode)
    }

    return (
      <StyledItinerary
        className={itineraryLevelCssClasses}
        data-id={position}
        bestValueFlight={bestValueFlight}
      >
        <MainZone key={'main-zone'} data-test='itinerary-shelf-main-zone'>
          <ItineraryInfo
            data-test='itinerary-information'
            numberOfUpsells={upsells.length}
            shouldShowBottomZone={shouldShowBottomZone}
            width={shelfVariantName === 'THREESHELVES' ? '45%' : '50%'}
          >
            <StyledItinSlices data-test='slices-section'>
              <Flex>
                <Box m={2} ml={3}>
                  <Text bold fontSize={0}>
                    {getStopText(itinerary.slices[0].stops)}
                  </Text>
                </Box>
                <Box m={2}>
                  <Text color='text.light' fontSize={0}>
                    {itinerary.slices[0].duration}
                  </Text>
                </Box>
              </Flex>
              <Box>{slices}</Box>
              <Box width={1 / 2}>
                <OperatedBy
                  pl={12}
                  operatedByAirlines={operatedByAirlines}
                  isSubjectToGovtApproval={isSubjectToGovtApproval}
                />
              </Box>
              <GovernmentApproval
                isSubjectToGovtApproval={isSubjectToGovtApproval}
                data-test='government-approval'
              >
                Subject to Government Approval
              </GovernmentApproval>
              {!missingBrandAttributes && (
                <Absolute bottom={7} right={12}>
                  <CompareOptionsModal
                    upsells={getCompareModalData(
                      shelfVariantName === 'TWOSHELVES' ? fareBrands.slice(0, 2) : fareBrands
                    )}
                    callToDetails={handleClick}
                    flyGaLogic={flyGaLogic}
                  />
                </Absolute>
              )}
            </StyledItinSlices>
            {merchandisingSection}
          </ItineraryInfo>

          <FareBrandShelf
            data-autobot-element-id='fare-brands-shelf'
            upsells={fareBrands}
            baseFarePrice={baseFare}
            brandReference={brandReference}
            itinerary={itinerary}
            priceDisplay={totalPriceWithDecimal}
            incrementalPrice={itinerary.incrementalPrice}
            gaIndex={gaIndex}
            onSelect={handleClick}
            tripType={tripType}
            isFused={isFused}
            itinAirlines={itinerary.airlines}
            fusedFareBrandOptions={fusedFareBrandOptions}
            shelfVariantName={shelfVariantName}
          />
        </MainZone>
        {shouldShowBottomZone && (
          <ItineraryShelfBottomZone
            showDeltaFreeMessaging={itinerary.showDeltaFreeMessaging}
            freeCancellationMessage={freeCancellationMessage}
          />
        )}
      </StyledItinerary>
    )
  }
}

ItineraryShelf.defaultProps = {
  listingIsLoading: false,
  brandReference: [],
  slices: [],
}

ItineraryShelf.propTypes = {
  itinerary: PropTypes.object.isRequired,
  gaIndex: PropTypes.number.isRequired,
  position: PropTypes.number.isRequired,
  openLoginModal: PropTypes.func,
  airportToCity: PropTypes.object,
  listingsSaleStyle: PropTypes.shape({
    bg: PropTypes.string,
    badgeText: PropTypes.string,
    copyText: PropTypes.string,
  }),
  brandReference: PropTypes.array,
  hasNoHoverTooltipCookie: PropTypes.bool,
  isUserLoggedIn: PropTypes.bool,
  setNoTooltipOnHoverCookie: PropTypes.func,
  slices: PropTypes.array,
  searchRequestCabinClass: PropTypes.string,
  departingFrom: PropTypes.object,
  shelfVariantName: PropTypes.string,
}

ItineraryShelf.contextTypes = {
  onSelect: PropTypes.func,
  SETI: PropTypes.object,
  flyGaLogic: PropTypes.object,
}

ItineraryShelf.childContextTypes = {
  gaIndex: PropTypes.number,
}

export default ItineraryShelf
